import { closeHistory } from 'prosemirror-history'
import { Node } from 'prosemirror-model'
import { Command, EditorState } from 'prosemirror-state'

import { RevisionStep } from '@showrunner/codex'
import { schema } from '@showrunner/prose-schemas'

import { BlockFormats } from '#util/formats'
import { getBlockHeightInfo } from '#util/pagination/helpers'

import { getConfigData } from './configData'
import {
  getRevisionsState,
  latestRevision,
  revisionId,
  splitRangeIntoSpans,
} from './revisions'

export const toggleLineRevision: Command = (state, dispatch) => {
  const activeLine = getLineSelection(state)
  if (!activeLine) return false
  const { from, to } = activeLine
  return updateRevision(from, to)(state, dispatch)
}

function getLineSelection(state: EditorState) {
  const { selection } = state
  const { $from, $to, from, to } = selection
  if (from !== to) return { from, to }

  const range = $from.blockRange($to, (node) => node.type === schema.nodes.page)
  if (!range || range.endIndex - range.startIndex > 1) return null
  const block = range.parent.child(range.startIndex)
  const { currentBlockFormats } = getConfigData(state)
  const line = getLine(block, currentBlockFormats, range.start, from)
  if (!line || from === line.from || to === line.to) return null

  return line
}

function getLine(
  node: Node,
  currentBlockFormats: BlockFormats,
  offset: number,
  pos: number,
) {
  const { sections = [] } = getBlockHeightInfo(node, currentBlockFormats)
  let sectionStart = 0
  for (const section of sections) {
    for (const line of section) {
      const lineFrom = line.from + sectionStart + offset + 1
      const lineTo = line.to + sectionStart + offset + 1
      if (lineFrom <= pos && lineTo >= pos) {
        return { from: lineFrom, to: lineTo }
      }
    }
    sectionStart += (section.at(-1)?.to ?? 0) + 1
  }
  return null
}

function updateRevision(from: number, to: number): Command {
  return (state, dispatch) => {
    const pluginState = getRevisionsState(state)

    if (pluginState.tag !== 'Resolved') return false
    if (!pluginState.revisionsState.enabled) return false
    if (from === to) return false

    const activeRevisionId = revisionId(
      pluginState.revisionsState.revisions.active,
    )
    const spans = pluginState.tracker.changesBetween(from, to)
    const splitSpans = splitRangeIntoSpans(from, to, spans)
    if (!splitSpans.length) return false
    const latest = latestRevision(
      splitSpans,
      pluginState.revisionsState.revisionMap,
    )
    const latestRevisionId = latest ? revisionId(latest) : undefined

    const latestRevisionSpans = splitSpans.filter(
      (span) => span.revision === latestRevisionId,
    )

    const tr = state.tr
    if (!latestRevisionSpans.length) {
      tr.step(RevisionStep.create(from, to, null, activeRevisionId))
    } else {
      latestRevisionSpans.forEach((span) => {
        tr.step(RevisionStep.create(span.from, span.to, span.revision, null))
      })
    }
    closeHistory(tr)
    dispatch?.(tr)
    return true
  }
}
